import axios from 'axios';
import { showErrorSnackbar } from './commons/commonFunctions';

class Api {
  token: string | null | any;
  root: string | undefined;
  errorOperationMessage = 'Error occurred while performing this operation.';
  errorFetchMessage = 'Error occurred while retrieving the data.';
  constructor() {
    /**
     * Api.ts
     *
     * Centralized API service for all backend requests using axios.
     * Handles authentication, error handling, and request formatting for all endpoints.
     * Each method returns a standardized response object with success and data.
     */
    this.token = sessionStorage.getItem('loginToken') ?? false;
    this.root = process.env.REACT_APP_PLATFORM_BASE_URL;
  }

  /**
   * Returns axios header options with Authorization token for API requests.
   * Reads the latest login token from sessionStorage and extracts the accessToken.
   * Used to authenticate all backend requests.
   */
  getAuthOptions() {
    this.token = sessionStorage.getItem('loginToken') ?? false;
    const { accessToken } = JSON.parse(this.token) || {};
    return {
      headers: {
        Authorization: `Bearer ${accessToken?.accessToken}`,
      },
    };
  }

  async getClientByDomain(payload: any) {
    try {
      const response = await axios.get(`${this.root}/clients/api/v2/clients?subDomain=${payload}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches leader profile data by email and clientId.
   * @param payload Object containing email and clientId
   * @returns API response with leader profile data
   */
  async getLeaderProfile(payload: any) {
    try {
      const response = await axios.get(`${this.root}/profile/api/v2/leader-profile?email=${encodeURIComponent(payload.email)}&clientId=${payload.clientId}&includeOktaStatus=true`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Updates leader profile data by leaderProfileId.
   * @param payload Object containing updated profile fields
   * @param leaderProfileId ID of the leader profile to update
   * @returns API response with update status
   */
  async updateLeaderProfile(payload: any, leaderProfileId: any) {
    try {
      const response = await axios.patch(`${this.root}/profile/api/v2/leader-profile/${leaderProfileId}`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches lookup values for a client (e.g., dropdowns, enums).
   * @param payload Object containing clientId
   * @returns API response with lookup data
   */
  async getLookUp(payload: any) {
    try {
      const response = await axios.get(`${this.root}/leadership/api/v2/lookups?clientId=${payload.clientId}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches all projects/assignments for a leader profile.
   * @param clientId Client ID
   * @param leaderProfileId Leader profile ID
   * @returns API response with project assignments
   */
  async getLeaderProjects(clientId: any, leaderProfileId: any) {
    try {
      const response = await axios.get(
        `${this.root}/projects/api/v2/assignments?clientId=${clientId}&leaderProfileId=${leaderProfileId}&includeRaters=true`,
        this.getAuthOptions()
      );
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches all assessment details for a leader profile.
   * @param clientId Client ID
   * @param leaderProfileId Leader profile ID
   * @returns API response with assessment details
   */
  async getLeaderAssessmentDetails(clientId: any, leaderProfileId?: any) {
    try {
      let url = `${this.root}/assessments/api/v2/assessments?clientId=${clientId}`;
      if (leaderProfileId != null && leaderProfileId !== 'None') {
        url += `&leaderProfileId=${leaderProfileId}`;
      }
      const response = await axios.get(url, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches rater assessment details for a participant (by email).
   * @param clientId Client ID
   * @param participantEmail Email of the participant/rater
   * @returns API response with rater assessment details
   */
  async getRaterAssessment(clientId: any, participantEmail: any) {
    try {
      const response = await axios.get(
        `${this.root}/assessments/api/v2/assessments?clientId=${clientId}&participantEmail=${encodeURIComponent(participantEmail)}&assessmentType=rater-assessment`,
        this.getAuthOptions()
      );
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches assessment structure details for a list of assessment IDs.
   * @param idsString Comma-separated string of assessment IDs
   * @returns API response with assessment structure details
   */
  async getLeaderAssessmentStructureDetails(idsString: string, limit: number) {
    try {
      const response = await axios.get(`${this.root}/assessmentstructure/api/v2/assessments?assessmentIdList=${idsString}&limit=${limit}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Fetches all report attachments for a leader profile.
   * @param leaderProfileId Leader profile ID
   * @returns API response with report attachments
   */
  async getLeaderReports(leaderProfileId: any) {
    try {
      const response = await axios.get(`${this.root}/attachments/api/v2/attachments?leaderProfileId=${leaderProfileId}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  /**
   * Downloads a leader report attachment by ID and triggers browser download.
   * Handles file extension based on content type.
   * @param attachmentId Attachment ID
   * @param reportName Desired file name (without extension)
   * @returns API response with download status
   */
  async downloadLeaderReports(attachmentId: any, reportName: string) {
    const content_type_mapping: any = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/rtf': 'rtf',
      'text/plain': 'txt',
    };
    let options: any = this.getAuthOptions();
    options['responseType'] = 'blob';
    try {
      const response = await axios.get(`${this.root}/attachments/api/v2/attachments/${attachmentId}/download`, options);
      const url_2 = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url_2;
      const contentType:any = response.headers['content-type'];
      const fileExt = content_type_mapping[contentType];
      const fileName = reportName + '.' + fileExt;
      link.setAttribute('download', fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  /**
   * Verifies an invite token for a user (SSA flow).
   * @param payload Object containing invite verification data
   * @returns API response with verification status
   */
  async inviteVerify(payload: any) {
    try {
      const response = await axios.post(`${this.root}/ssa/api/v2/invite-verify`, payload);
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async inviteVerifyNew(payload: any) {
    try {
      const response = await axios.post(`${this.root}/ssa/api/v2/invite-token-verify`, payload);
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      return { success: false, data };
    }
  }

  async setPassword(payload: any, token: any) {
    try {
      const response = await axios.post(`${this.root}/auth/api/v2/set-password`, payload, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      //showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async getEmailVerify(payload: any, token: any) {
    try {
      const response = await axios.post(`${this.root}/auth/api/v2/register`, payload, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async addRaters(payload: any) {
    try {
      const response = await axios.post(`${this.root}/projects/api/v2/add-rater`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      return { success: false, data };
    }
  }

  async downloadAttachments(attachmentId: string) {
    try {
      const response = await axios.get(`${this.root}/attachments/api/v2/attachments/${attachmentId}/download?preSignedUrl=Y`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      return { success: false, data };
    }
  }

  async inviteRaters(payload: any) {
    try {
      const response = await axios.post(`${this.root}/emails/api/v2/assessment-notification`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async generateAssessmentLinks(payload: any) {
    try {
      const response = await axios.post(`${this.root}/assessments/api/v2/360/invite`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async updateAssessmentsStatus(payload: any, assessmentId: any) {
    try {
      const response = await axios.patch(`${this.root}/assessments/api/v2/assessments/${assessmentId}`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async updateProjectAssignmentAssessmentsStatus(payload: any, assignmentId: any) {
    try {
      const response = await axios.patch(`${this.root}/projects/api/v2/assignments/${assignmentId}`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async updateOktaStatus(payload: any) {
    try {
      const response = await axios.post(`${this.root}/auth/api/v2/identity-groups`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async updatePrepWorkCompletedFlag(assessmentId: number) {
    try {
      const response = await axios.post(`${this.root}/assessments/api/v2/assessments/complete-prep/${assessmentId}`, {}, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async getVideoURL(url: any) {
    try {
      const response = await axios.get(`${url}`, {});
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }
 
  async downloadImmersivePdf(attachmentId: string) {
    const content_type_mapping: any = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/rtf': 'rtf',
      'text/plain': 'txt',
    };
    let options: any = this.getAuthOptions();
    options['responseType'] = 'blob';
    try {
      const metaResp = await axios.get(
        `${this.root}/attachments/api/v2/attachments/${attachmentId}/download`,
        options
      );

      const url_2 = window.URL.createObjectURL(new Blob([metaResp.data], { type: 'application/pdf' }));
     
      const isSafeBlobUrl = /^blob:/i.test(url_2);
      if (!isSafeBlobUrl) {
        showErrorSnackbar({ content: this.errorOperationMessage });
        return { success: false, data: { message: 'Invalid file URL generated.' } };
      }

      const link = document.createElement('a');
      link.href = url_2;
      const contentType = 'application/pdf'; // Assuming the content type is PDF, adjust if needed
      const fileExt = content_type_mapping[contentType];
      const fileName = 'BriefingDocument.' + fileExt;
      link.setAttribute('download', fileName); //or any other extension
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(url_2);
      }, 1000);
      return { success: true, response: metaResp };

    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }

    
  }

  //api for downloading privacy policy pdf for immersive assessment, used in welcome page and onboarding page replacing URL link
  async downloadAssessmentprivacyPolicyPDF(attachmentId: string) {
    let options: any = this.getAuthOptions();
    options['responseType'] = 'blob';
    try {
      const metaResp = await axios.get(
        `${this.root}/attachments/api/v2/attachments/${attachmentId}/download`,
        options
      );
      const blob = new Blob([metaResp.data], { type: 'application/pdf' });
      const url_2 = URL.createObjectURL(blob);

      const isSafeBlobUrl = /^blob:/i.test(url_2);
      if (!isSafeBlobUrl) {
        showErrorSnackbar({ content: this.errorOperationMessage });
        return { success: false, data: { message: 'Invalid file URL generated.' } };
      }

      const link = document.createElement('a');
      link.href = url_2;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        URL.revokeObjectURL(url_2);
      }, 1000);
      
      return { success: true, response: metaResp };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  //Get Attachment details
  async getAttachmentDetails(attachmentId: any) {
    try {
      const response = await axios.get(`${this.root}/attachments/api/v2/attachments/${attachmentId}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async updateBulkRatersStatus(payload: any, id: any) {
    try {
      const response = await axios.patch(`${this.root}/projects/api/v2/bulk_raters/${id}`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async updateProjectAssignmentRatersStatus(payload: any, id: any) {
    try {
      const response = await axios.patch(`${this.root}/projects/api/v2/raters/${id}`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async assignAassessmentAndSendInvitation(payload: any) {
    try {
      const response = await axios.post(`${this.root}/projects/api/v2/assign-assessments`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async fetchMayoTemplateDetails(templateId: string) {
    try {
      const response = await axios.get(`${this.root}/assessmentstructure/api/v2/templates/${templateId}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async getStatementData(payload: any) {
    try {
      //67052054e32accfcfdd2fbcc -- have data
      //6a732e44c2980c33af7f404b -- Need to use this client for now
      const response = await axios.get(`${this.root}/leadership/api/v2/statement-libraries?clientId=${payload.clientId}&statementCodeList=${payload.statementCodeList}&status=Published`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async getRatingScaleData(payload: any) {
    try {
      const response = await axios.get(`${this.root}/leadership/api/v2/response-options?clientId=${payload.clientId}&code=${payload.RSCodeList}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async fetchAssessmentQuestionsAnswered(assessmentId: any) {
    try {
      const response = await axios.get(`${this.root}/assessments/api/v2/assessments/${assessmentId}`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }

  async saveAndCompleteAssessment(payload: any) {
    try {
      const response = await axios.post(`${this.root}/assessments/api/v2/assessment-completed`, payload, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorFetchMessage });
      return { success: false, data };
    }
  }

  async getUserDetails() {
    try {
      const response = await axios.get(`${this.root}/auth/api/v2/me`, this.getAuthOptions());
      return { success: true, response };
    } catch (error: any) {
      const { response: response_2 } = error;
      const { data } = response_2;
      showErrorSnackbar({ content: this.errorOperationMessage });
      return { success: false, data };
    }
  }
}

export default new Api();
